/**
 * Generated by orval 🍺
 * Do not edit manually.
 * [DEV] Backend
 * OpenAPI spec version: 0.1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  CreateOrganizationReq,
  CreateOrganizationResp,
  GetReportsParams,
  GetUserOrganizationsResp,
  HTTPValidationError,
  OrganizationAggregatesReq,
  OrganizationAggregatesResp,
  OrganizationReportsResp,
  ResponseMessage,
  UpdateOrganizationDefaultAggregateByReq,
  UpdateOrganizationDefaultAggregateByResp,
  UpdateOrganizationReq,
} from '../model';

import { customFetch } from '../../fetcher';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export const getCreateOrganizationUrl = () => {
  return `/api/v1/organizations`;
};

/**
 * Creates a new organization. Error if the organization name already exists
 * @summary Create Organization
 */
export const createOrganization = async (
  createOrganizationReq: CreateOrganizationReq,
  options?: Parameters<typeof customFetch>[1],
): Promise<CreateOrganizationResp> => {
  const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return customFetch<CreateOrganizationResp>(getCreateOrganizationUrl(), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createOrganizationReq),
  });
};

export const getCreateOrganizationMutationOptions = <TError = HTTPValidationError, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createOrganization>>,
    TError,
    CreateOrganizationMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createOrganization>>,
  TError,
  CreateOrganizationMutationVariables,
  TContext
> => {
  const mutationKey = ['createOrganization'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createOrganization>>,
    CreateOrganizationMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return createOrganization(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateOrganizationMutationResult = NonNullable<Awaited<ReturnType<typeof createOrganization>>>;
export type CreateOrganizationMutationBody = CreateOrganizationReq;
export type CreateOrganizationMutationError = HTTPValidationError;
export type CreateOrganizationMutationVariables = { data: CreateOrganizationReq };

/**
 * @summary Create Organization
 */
export const useCreateOrganization = <TError = HTTPValidationError, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createOrganization>>,
    TError,
    CreateOrganizationMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createOrganization>>,
  TError,
  CreateOrganizationMutationVariables,
  TContext
> => {
  return useMutation(getCreateOrganizationMutationOptions(options));
};
export const getGetOrganizationsUrl = () => {
  return `/api/v1/organizations`;
};

/**
 * @summary Get Organizations
 */
export const getOrganizations = async (
  options?: Parameters<typeof customFetch>[1],
): Promise<GetUserOrganizationsResp> => {
  return customFetch<GetUserOrganizationsResp>(getGetOrganizationsUrl(), {
    ...options,
    method: 'GET',
  });
};

export const getGetOrganizationsQueryKey = () => {
  return [`/api/v1/organizations`] as const;
};

export const getGetOrganizationsQueryOptions = <
  TData = Awaited<ReturnType<typeof getOrganizations>>,
  TError = HTTPValidationError,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof getOrganizations>>, TError, TData>;
  request?: SecondParameter<typeof customFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetOrganizationsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getOrganizations>>> = ({ signal }) =>
    getOrganizations({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getOrganizations>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetOrganizationsQueryResult = NonNullable<Awaited<ReturnType<typeof getOrganizations>>>;
export type GetOrganizationsQueryError = HTTPValidationError;

/**
 * @summary Get Organizations
 */

export function useGetOrganizations<
  TData = Awaited<ReturnType<typeof getOrganizations>>,
  TError = HTTPValidationError,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof getOrganizations>>, TError, TData>;
  request?: SecondParameter<typeof customFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetOrganizationsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getUpdateOrganizationUrl = (organizationId: string) => {
  return `/api/v1/organizations/${organizationId}`;
};

/**
 * Updates an organization by ID. Error if the organization name already exists
 * @summary Update Organization
 */
export const updateOrganization = async (
  organizationId: string,
  updateOrganizationReq: UpdateOrganizationReq,
  options?: Parameters<typeof customFetch>[1],
): Promise<CreateOrganizationResp> => {
  const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return customFetch<CreateOrganizationResp>(getUpdateOrganizationUrl(organizationId), {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateOrganizationReq),
  });
};

export const getUpdateOrganizationMutationOptions = <TError = HTTPValidationError, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateOrganization>>,
    TError,
    UpdateOrganizationMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateOrganization>>,
  TError,
  UpdateOrganizationMutationVariables,
  TContext
> => {
  const mutationKey = ['updateOrganization'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateOrganization>>,
    UpdateOrganizationMutationVariables
  > = (props) => {
    const { organizationId, data } = props ?? {};

    return updateOrganization(organizationId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateOrganizationMutationResult = NonNullable<Awaited<ReturnType<typeof updateOrganization>>>;
export type UpdateOrganizationMutationBody = UpdateOrganizationReq;
export type UpdateOrganizationMutationError = HTTPValidationError;
export type UpdateOrganizationMutationVariables = { organizationId: string; data: UpdateOrganizationReq };

/**
 * @summary Update Organization
 */
export const useUpdateOrganization = <TError = HTTPValidationError, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateOrganization>>,
    TError,
    UpdateOrganizationMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateOrganization>>,
  TError,
  UpdateOrganizationMutationVariables,
  TContext
> => {
  return useMutation(getUpdateOrganizationMutationOptions(options));
};
export const getDeleteOrganizationUrl = (organizationId: string) => {
  return `/api/v1/organizations/${organizationId}`;
};

/**
 * Deletes an organization by ID. Error if the organization is not found
 * @summary Delete Organization
 */
export const deleteOrganization = async (
  organizationId: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<ResponseMessage> => {
  return customFetch<ResponseMessage>(getDeleteOrganizationUrl(organizationId), {
    ...options,
    method: 'DELETE',
  });
};

export const getDeleteOrganizationMutationOptions = <TError = HTTPValidationError, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteOrganization>>,
    TError,
    DeleteOrganizationMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteOrganization>>,
  TError,
  DeleteOrganizationMutationVariables,
  TContext
> => {
  const mutationKey = ['deleteOrganization'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteOrganization>>,
    DeleteOrganizationMutationVariables
  > = (props) => {
    const { organizationId } = props ?? {};

    return deleteOrganization(organizationId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteOrganizationMutationResult = NonNullable<Awaited<ReturnType<typeof deleteOrganization>>>;

export type DeleteOrganizationMutationError = HTTPValidationError;
export type DeleteOrganizationMutationVariables = { organizationId: string };

/**
 * @summary Delete Organization
 */
export const useDeleteOrganization = <TError = HTTPValidationError, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteOrganization>>,
    TError,
    DeleteOrganizationMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteOrganization>>,
  TError,
  DeleteOrganizationMutationVariables,
  TContext
> => {
  return useMutation(getDeleteOrganizationMutationOptions(options));
};
export const getUpdateOrganizationDefaultAggregateByUrl = (organizationId: string) => {
  return `/api/v1/organizations/${organizationId}/default-aggregate-by`;
};

/**
 * Updates the default aggregate by for an organization
 * @summary Update Organization Default Aggregate By
 */
export const updateOrganizationDefaultAggregateBy = async (
  organizationId: string,
  updateOrganizationDefaultAggregateByReq: UpdateOrganizationDefaultAggregateByReq,
  options?: Parameters<typeof customFetch>[1],
): Promise<UpdateOrganizationDefaultAggregateByResp> => {
  const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return customFetch<UpdateOrganizationDefaultAggregateByResp>(
    getUpdateOrganizationDefaultAggregateByUrl(organizationId),
    {
      ...options,
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
      body: JSON.stringify(updateOrganizationDefaultAggregateByReq),
    },
  );
};

export const getUpdateOrganizationDefaultAggregateByMutationOptions = <
  TError = HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateOrganizationDefaultAggregateBy>>,
    TError,
    UpdateOrganizationDefaultAggregateByMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateOrganizationDefaultAggregateBy>>,
  TError,
  UpdateOrganizationDefaultAggregateByMutationVariables,
  TContext
> => {
  const mutationKey = ['updateOrganizationDefaultAggregateBy'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateOrganizationDefaultAggregateBy>>,
    UpdateOrganizationDefaultAggregateByMutationVariables
  > = (props) => {
    const { organizationId, data } = props ?? {};

    return updateOrganizationDefaultAggregateBy(organizationId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateOrganizationDefaultAggregateByMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateOrganizationDefaultAggregateBy>>
>;
export type UpdateOrganizationDefaultAggregateByMutationBody = UpdateOrganizationDefaultAggregateByReq;
export type UpdateOrganizationDefaultAggregateByMutationError = HTTPValidationError;
export type UpdateOrganizationDefaultAggregateByMutationVariables = {
  organizationId: string;
  data: UpdateOrganizationDefaultAggregateByReq;
};

/**
 * @summary Update Organization Default Aggregate By
 */
export const useUpdateOrganizationDefaultAggregateBy = <TError = HTTPValidationError, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateOrganizationDefaultAggregateBy>>,
    TError,
    UpdateOrganizationDefaultAggregateByMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateOrganizationDefaultAggregateBy>>,
  TError,
  UpdateOrganizationDefaultAggregateByMutationVariables,
  TContext
> => {
  return useMutation(getUpdateOrganizationDefaultAggregateByMutationOptions(options));
};
export const getGetAggregatesUrl = () => {
  return `/api/v1/organizations/aggregates`;
};

/**
 * @summary Get Organization Aggregates
 */
export const getAggregates = async (
  organizationAggregatesReq: OrganizationAggregatesReq,
  options?: Parameters<typeof customFetch>[1],
): Promise<OrganizationAggregatesResp> => {
  const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return customFetch<OrganizationAggregatesResp>(getGetAggregatesUrl(), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(organizationAggregatesReq),
  });
};

export const getGetAggregatesMutationOptions = <TError = HTTPValidationError, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof getAggregates>>,
    TError,
    GetAggregatesMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationOptions<Awaited<ReturnType<typeof getAggregates>>, TError, GetAggregatesMutationVariables, TContext> => {
  const mutationKey = ['getAggregates'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof getAggregates>>, GetAggregatesMutationVariables> = (
    props,
  ) => {
    const { data } = props ?? {};

    return getAggregates(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type GetAggregatesMutationResult = NonNullable<Awaited<ReturnType<typeof getAggregates>>>;
export type GetAggregatesMutationBody = OrganizationAggregatesReq;
export type GetAggregatesMutationError = HTTPValidationError;
export type GetAggregatesMutationVariables = { data: OrganizationAggregatesReq };

/**
 * @summary Get Organization Aggregates
 */
export const useGetAggregates = <TError = HTTPValidationError, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof getAggregates>>,
    TError,
    GetAggregatesMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof customFetch>;
}): UseMutationResult<Awaited<ReturnType<typeof getAggregates>>, TError, GetAggregatesMutationVariables, TContext> => {
  return useMutation(getGetAggregatesMutationOptions(options));
};
export const getGetReportsUrl = (params?: GetReportsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/organizations/reports?${stringifiedParams}`
    : `/api/v1/organizations/reports`;
};

/**
 * @summary Get Organization Reports
 */
export const getReports = async (
  params?: GetReportsParams,
  options?: Parameters<typeof customFetch>[1],
): Promise<OrganizationReportsResp> => {
  return customFetch<OrganizationReportsResp>(getGetReportsUrl(params), {
    ...options,
    method: 'GET',
  });
};

export const getGetReportsQueryKey = (params?: GetReportsParams) => {
  return [`/api/v1/organizations/reports`, ...(params ? [params] : [])] as const;
};

export const getGetReportsQueryOptions = <TData = Awaited<ReturnType<typeof getReports>>, TError = HTTPValidationError>(
  params?: GetReportsParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getReports>>, TError, TData>;
    request?: SecondParameter<typeof customFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetReportsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getReports>>> = ({ signal }) =>
    getReports(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getReports>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetReportsQueryResult = NonNullable<Awaited<ReturnType<typeof getReports>>>;
export type GetReportsQueryError = HTTPValidationError;

/**
 * @summary Get Organization Reports
 */

export function useGetReports<TData = Awaited<ReturnType<typeof getReports>>, TError = HTTPValidationError>(
  params?: GetReportsParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getReports>>, TError, TData>;
    request?: SecondParameter<typeof customFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetReportsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}
